import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  EpisodeRef,
  OutputRef,
  StoryboardView,
  WorldImage,
  WorldImageKind,
  WorldNode,
  WorldRule,
  WorldView,
} from "@dramapie/shared";

import { deleteWorldImage, uploadWorldImage } from "../../lib/api";
import { errorMessage } from "../../lib/errors";
import { formatClock, formatDate } from "../../lib/format";
import {
  WORLD_OTHER_IMAGE_KINDS,
  worldImageGroupNote,
  worldImageGroups,
  type WorldImageItem,
} from "../../lib/worldImages";
import {
  firstMatchedSceneNode,
  sceneIndex,
  scenesForNode,
  worldChildren,
  worldDepth,
  worldNodeById,
  worldNodeImages,
  worldOutputOptions,
  worldPath,
  worldRoots,
  worldRuleScopeNames,
  worldVisibility,
  type WorldSceneAsset,
} from "../../lib/world";
import { useImageViewer } from "../media/ImageViewer";

/**
 * 世界（资料库第一个一级 tab 的内容）。
 *
 * 数据是后端 M7 真模型 `WorldView`：`nodes` 按 `parentId` 建树、`order` 排序；
 * 场次不复制，靠 `node.sceneIds` join 到 `storyboard.scenes`。
 *
 * 布局照原型：左侧完整树（展开/收起/搜索 + 产出线 / 集数两个下拉），
 * 右侧 = 面包屑 + 节点标题 + 统计 + 景观图 + 子节点分区 + 该节点下的场景资产。
 * 过滤只影响显隐高亮，不重组树；命中节点的上级保留（dim），并自动选中
 * 第一个命中的“带场景”节点。
 */

const WORLD_LEVEL_LABEL: Record<WorldNode["level"], string> = {
  world: "世界",
  region: "区域",
  state: "州 / 国",
  place: "场所",
  scene: "场景",
};

export interface WorldLibraryProps {
  world: WorldView;
  storyboard?: StoryboardView | null;
  /** 项目产出线：把 `node.outputs` 里的 id 翻成标题。 */
  outputs?: readonly OutputRef[];
  /** 项目集列表：集数下拉的数据源。 */
  episodes?: readonly EpisodeRef[];
  /** 打开时定位到的节点（资料库按场景 chip / 资产版本「查看」进来）。 */
  initialNodeId?: string | null;
  /** 打开时直接看的场景。 */
  initialSceneId?: string | null;
  /** 「在故事板看这一场」。没接线时不渲染按钮。 */
  onOpenStoryboard?: (sceneId: string, episodeNo: number) => void;
  /** 空态里的「用 Agent 生成」。没接线时不渲染按钮。 */
  onGenerate?: () => void;
  /** 项目 id：有它才允许上传 / 删除设定图（世界树写回走提交闸门）。 */
  projectId?: string;
  /** 上传 / 删除后的提示（宿主已经有 toast 时接这里）。 */
  onNotify?: (kind: "success" | "error" | "info", message: string) => void;
}

type WorldTab = "places" | "rules";

function EpisodeTag({ no }: { no: number }) {
  return <span className="dp-wt-ep">EP{no}</span>;
}

export function WorldLibrary({
  world,
  storyboard = null,
  outputs = [],
  episodes = [],
  initialNodeId = null,
  initialSceneId = null,
  onOpenStoryboard,
  onGenerate,
  projectId,
  onNotify,
}: WorldLibraryProps) {
  const nodes = world.nodes;
  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node] as const)), [nodes]);
  const children = useMemo(() => worldChildren(nodes), [nodes]);
  const roots = useMemo(() => worldRoots(nodes), [nodes]);
  const sceneLookup = useMemo(() => sceneIndex(storyboard), [storyboard]);
  const outputOptions = useMemo(() => worldOutputOptions(nodes, outputs), [nodes, outputs]);

  const defaultNodeId = useMemo(() => {
    if (initialNodeId && byId.has(initialNodeId)) return initialNodeId;
    const withScene = nodes
      .filter((node) => node.sceneIds.length > 0)
      .sort((a, b) => worldDepth(nodes, b.id) - worldDepth(nodes, a.id))[0];
    return withScene?.id ?? roots[0]?.id ?? null;
  }, [initialNodeId, byId, nodes, roots]);

  const imageViewer = useImageViewer();
  const [tab, setTab] = useState<WorldTab>("places");
  const [query, setQuery] = useState("");
  const [ruleQuery, setRuleQuery] = useState("");
  const [outputId, setOutputId] = useState("all");
  const [episodeNo, setEpisodeNo] = useState<number | null>(null);
  const [nodeId, setNodeId] = useState<string | null>(defaultNodeId);
  const [closedIds, setClosedIds] = useState<ReadonlySet<string>>(new Set());
  const [openSceneId, setOpenSceneId] = useState<string | null>(initialSceneId);
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // 数据换项目 / 首次加载：把失效的选中节点拉回默认位置。
  useEffect(() => {
    setNodeId((current) => (current && byId.has(current) ? current : defaultNodeId));
  }, [byId, defaultNodeId]);

  const filter = useMemo(
    () => ({ query, outputId, episodeNo }),
    [query, outputId, episodeNo],
  );
  const { matched, visible } = useMemo(
    () => worldVisibility(nodes, filter, sceneLookup),
    [nodes, filter, sceneLookup],
  );

  // 过滤变化：当前节点没命中就自动选中第一个命中场景（不重组树）。
  const filterSignature = `${query}|${outputId}|${episodeNo ?? "all"}`;
  const lastFilter = useRef(filterSignature);
  useEffect(() => {
    if (lastFilter.current === filterSignature) return;
    lastFilter.current = filterSignature;
    setOpenSceneId(null);
    const current = nodeId ? byId.get(nodeId) : null;
    if (current && matched.has(current.id)) return;
    const pick = firstMatchedSceneNode(nodes, filter, sceneLookup);
    if (pick) setNodeId(pick.id);
  }, [filterSignature, nodeId, byId, matched, nodes, filter, sceneLookup]);

  const node = worldNodeById(nodes, nodeId) ?? roots[0] ?? null;
  const path = node ? worldPath(nodes, node.id) : [];
  const childNodes = node ? (children.get(node.id) ?? []) : [];
  const nodeScenes = node ? scenesForNode(nodes, sceneLookup, node.id) : [];
  const images = node ? worldNodeImages(node) : [];
  const unresolved = node ? Math.max(0, countSceneRefs(nodes, node.id) - nodeScenes.length) : 0;
  const relatedRules = node
    ? world.rules.filter((rule) => rule.body.includes(node.name) || rule.title.includes(node.name))
    : [];
  const isDirect = node ? matched.has(node.id) : false;
  const filterLabel =
    outputId === "all"
      ? "全部"
      : (outputOptions.find((output) => output.id === outputId)?.title ?? outputId);
  const openScene: WorldSceneAsset | null =
    nodeScenes.find((scene) => scene.sceneId === openSceneId) ?? null;
  const forceOpen = query.trim().length > 0 || episodeNo !== null;

  function toggle(id: string) {
    setClosedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectNode(id: string) {
    setNodeId(id);
    setOpenSceneId(null);
  }

  function renderTree(id: string): React.ReactNode {
    const item = byId.get(id);
    if (!item || !visible.has(id)) return null;
    const kids = (children.get(id) ?? []).filter((child) => visible.has(child.id));
    const active = node?.id === item.id;
    const isOpen = forceOpen || !closedIds.has(item.id);
    const classes = [
      "dp-wt-branch",
      active ? "is-active" : "",
      matched.has(id) ? "" : "is-dim",
      isOpen ? "is-open" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const episodeTags = item.firstChapter === null ? [] : [item.firstChapter];

    if (kids.length > 0) {
      return (
        <div key={item.id} className={classes}>
          <div className="dp-wt-row">
            <button
              type="button"
              className="dp-wt-caret"
              aria-label={`${isOpen ? "收起" : "展开"} ${item.name}`}
              aria-expanded={isOpen}
              onClick={() => toggle(item.id)}
            />
            <i className={`dp-wt-dot lv-${item.level}`} aria-hidden="true" />
            <button type="button" className="dp-wt-name" onClick={() => selectNode(item.id)}>
              {item.name}
            </button>
            {episodeTags.map((no) => (
              <EpisodeTag key={`${item.id}-ep-${no}`} no={no} />
            ))}
            <span className="dp-wt-count">{kids.length}</span>
          </div>
          {isOpen ? (
            <div className="dp-wt-children">{kids.map((child) => renderTree(child.id))}</div>
          ) : null}
        </div>
      );
    }

    return (
      <button
        key={item.id}
        type="button"
        className={`dp-wt-leaf ${active ? "is-active" : ""} ${matched.has(id) ? "" : "is-dim"}`}
        aria-current={active ? "true" : undefined}
        onClick={() => selectNode(item.id)}
      >
        <i className={`dp-wt-dot lv-${item.level}`} aria-hidden="true" />
        <span>{item.name}</span>
        {episodeTags.map((no) => (
          <EpisodeTag key={`${item.id}-ep-${no}`} no={no} />
        ))}
      </button>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="dp-doc-empty">
        <p className="dp-doc-empty-title">还没有世界设定</p>
        <p className="dp-doc-empty-text">
          世界树由 <code>world/tree.json</code> 驱动；老项目可能还没有这份文档。
        </p>
        {onGenerate ? (
          <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
            用 Agent 生成
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="dp-world-shell">
      <div className="dp-world-tabs" role="tablist" aria-label="世界视图">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "places"}
          className={tab === "places" ? "is-active" : ""}
          onClick={() => setTab("places")}
        >
          地点 <span>{nodes.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "rules"}
          className={tab === "rules" ? "is-active" : ""}
          onClick={() => setTab("rules")}
        >
          世界规则 <span>{world.rules.length}</span>
        </button>
      </div>

      {tab === "rules" ? (
        <WorldRules
          rules={world.rules}
          outputs={outputs}
          query={ruleQuery}
          onQuery={setRuleQuery}
          expandedRule={expandedRule}
          onExpand={setExpandedRule}
          onGenerate={onGenerate}
        />
      ) : (
        <div className="dp-world-layout">
          <aside className="dp-world-tree" aria-label="世界树">
            <div className="dp-wt-head">
              <span>世界</span>
              <span>{nodes.length} 处</span>
            </div>
            <label className="dp-lib-search dp-wt-search">
              <span aria-hidden="true">⌕</span>
              <input
                ref={searchRef}
                value={query}
                placeholder="搜索地点 / 摘要…"
                aria-label="搜索地点 / 摘要"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <div className="dp-wt-select-row">
              <label className="dp-wt-pair">
                <span>产出线</span>
                <select
                  value={outputId}
                  aria-label="产出线"
                  onChange={(event) => {
                    const next = event.target.value;
                    setOutputId(next);
                    if (next !== "all" && episodeNo === null) {
                      const first = episodes[0];
                      if (first) setEpisodeNo(first.no);
                    }
                  }}
                >
                  <option value="all">全部</option>
                  {outputOptions.map((output) => (
                    <option key={output.id} value={output.id}>
                      {output.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dp-wt-pair">
                <span>集数</span>
                <select
                  value={episodeNo === null ? "all" : String(episodeNo)}
                  aria-label="集数"
                  disabled={episodes.length === 0}
                  onChange={(event) => {
                    const value = event.target.value;
                    setEpisodeNo(value === "all" ? null : Number(value));
                  }}
                >
                  <option value="all">全部集数</option>
                  {episodes.map((episode) => (
                    <option key={episode.id} value={episode.no}>
                      第{episode.no}话 · {episode.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="dp-wt-tree">
              {roots.filter((root) => visible.has(root.id)).map((root) => renderTree(root.id))}
              {roots.every((root) => !visible.has(root.id)) ? (
                <div className="dp-lib-empty">
                  当前筛选没有命中节点。
                  {episodeNo !== null ? `第${episodeNo}话还没有场次或节点。` : ""}
                </div>
              ) : null}
            </div>
            <p className="dp-wt-note">
              过滤只改显隐，上级节点保留作上下文；场景挂在节点下，不单列 tab。
            </p>
          </aside>

          {openScene ? (
            <section className="dp-world-main dp-scene-sheet" aria-label={`场景详情 ${openScene.name}`}>
              <button
                type="button"
                className="dp-btn sm ghost dp-scene-back"
                onClick={() => setOpenSceneId(null)}
              >
                ← 返回世界节点
              </button>
              <div className="dp-wm-title-row">
                <h2 className="dp-wm-title">{openScene.name}</h2>
                <span className="dp-wm-level">场景</span>
                <EpisodeTag no={openScene.episodeNo} />
              </div>
              <p className="dp-wm-summary">
                {openScene.time} · {openScene.shotCount} 镜 · {formatClock(openScene.duration)}
              </p>
              <div className="dp-scene-sheet-body">
                {openScene.coverUrl ? (
                  <button
                    type="button"
                    className="dp-scene-cover"
                    aria-label={`查看 ${openScene.name} 场次首帧`}
                    onClick={() =>
                      imageViewer.open([
                        {
                          src: openScene.coverUrl as string,
                          title: `${openScene.name} · 场次首帧`,
                          subtitle: "场次首帧",
                        },
                      ])
                    }
                  >
                    <img src={openScene.coverUrl} alt={`${openScene.name} 场次首帧`} />
                    <span>场次首帧</span>
                  </button>
                ) : (
                  <div className="dp-wm-empty">
                    这一场还没有首帧图；场景资产挂在世界节点下，不另开 tab。
                  </div>
                )}
                <dl className="dp-scene-facts">
                  <div>
                    <dt>所属节点</dt>
                    <dd>{node?.name ?? "—"}</dd>
                  </div>
                  <div>
                    <dt>集数</dt>
                    <dd>第{openScene.episodeNo}话</dd>
                  </div>
                  <div>
                    <dt>时间段</dt>
                    <dd>{openScene.time || "—"}</dd>
                  </div>
                  <div>
                    <dt>镜头</dt>
                    <dd>
                      {openScene.shotCount} 镜 · {formatClock(openScene.duration)}
                    </dd>
                  </div>
                </dl>
              </div>
              {onOpenStoryboard ? (
                <button
                  type="button"
                  className="dp-btn sm primary"
                  onClick={() => onOpenStoryboard(openScene.sceneId, openScene.episodeNo)}
                >
                  在故事板看这一场
                </button>
              ) : null}
            </section>
          ) : node ? (
            <section className="dp-world-main" aria-label={`世界节点 ${node.name}`}>
              <div className="dp-wm-crumb">
                {path.map((item, index) => (
                  <span key={item.id} className="dp-wm-crumb-item">
                    {index > 0 ? <span aria-hidden="true">›</span> : null}
                    <button type="button" onClick={() => selectNode(item.id)}>
                      {item.name}
                    </button>
                  </span>
                ))}
              </div>
              <div className="dp-wm-title-row">
                <h2 className="dp-wm-title">{node.name}</h2>
                <span className="dp-wm-level">{WORLD_LEVEL_LABEL[node.level]}</span>
              </div>
              <p className="dp-wm-summary">{node.summary ?? "—"}</p>
              <div className="dp-wm-stats">
                {node.firstChapter !== null ? (
                  <span>
                    <b>第 {node.firstChapter} 话</b>首见
                  </span>
                ) : null}
                <span>
                  <b>{childNodes.length}</b>子节点
                </span>
                <span>
                  <b>{nodeScenes.length}</b>场景资产
                  {nodeScenes.length > 0
                    ? ` · ${[...new Set(nodeScenes.map((scene) => `EP${scene.episodeNo}`))].join(" ")}`
                    : ""}
                </span>
                <span>
                  <b>{images.length}</b>设定图
                </span>
                {node.outputs.length > 0 ? <span>{node.outputs.length} 条产出线</span> : null}
                {!isDirect ? (
                  <span className="dp-wm-filter-warn">
                    当前「{filterLabel}」筛选下未直接命中，仅作上级上下文
                  </span>
                ) : null}
              </div>

              <div className="dp-wm-section">
                <div className="dp-wm-sec-head">
                  <h3>视觉设定</h3>
                  <span>全景图当场景背景；站位图只写进提示词</span>
                </div>
                <WorldImages
                  node={node}
                  {...(projectId ? { projectId } : {})}
                  {...(onNotify ? { onNotify } : {})}
                />
              </div>

              {childNodes.length > 0 ? (
                <div className="dp-wm-section">
                  <div className="dp-wm-sec-head">
                    <h3>子节点</h3>
                    <span>{childNodes.length} 处 · 点击进入</span>
                  </div>
                  <div className="dp-wm-child-grid">
                    {childNodes.map((child) => {
                      const childImages = worldNodeImages(child);
                      const first = childImages[0];
                      return (
                        <button
                          key={child.id}
                          type="button"
                          className="dp-wm-child"
                          onClick={() => selectNode(child.id)}
                        >
                          {first ? (
                            <img src={first.src} alt="" loading="lazy" />
                          ) : (
                            <div className="dp-wm-child-ph">{WORLD_LEVEL_LABEL[child.level]}</div>
                          )}
                          <div>
                            <b>{child.name}</b>
                            <span>{(child.summary ?? "").slice(0, 56) || "暂无摘要"}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="dp-wm-section">
                <div className="dp-wm-sec-head">
                  <h3>本节点下的场景资产</h3>
                  <span>场景直接挂在地点上，带集数标签</span>
                </div>
                {nodeScenes.length > 0 ? (
                  <>
                    <ul className="dp-wm-scene-grid">
                      {nodeScenes.map((scene) => (
                        <li key={scene.sceneId}>
                          <button
                            type="button"
                            className="dp-wm-scene"
                            aria-current={scene.sceneId === openSceneId ? "true" : undefined}
                            onClick={() => {
                              setOpenSceneId(scene.sceneId);
                            }}
                          >
                            {scene.coverUrl ? (
                              <img src={scene.coverUrl} alt="" loading="lazy" />
                            ) : (
                              <span className="dp-wm-scene-ph" aria-hidden="true">
                                {scene.name.slice(0, 1)}
                              </span>
                            )}
                            <div>
                              <b>{scene.name}</b>
                              <span>
                                EP{scene.episodeNo} · {scene.time} · {scene.shotCount} 镜
                              </span>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {unresolved > 0 ? (
                      <p className="dp-tiny-note">
                        另有 {unresolved} 个 sceneId 属于还没加载的集；切到该集后自动显示。
                      </p>
                    ) : null}
                  </>
                ) : (
                  <div className="dp-wm-empty">
                    这个节点还没有对应的可拍场景；它只是世界地图上的位置。
                  </div>
                )}
              </div>

              {relatedRules.length > 0 ? (
                <div className="dp-wm-section">
                  <div className="dp-wm-sec-head">
                    <h3>相关设定</h3>
                    <span>{relatedRules.length} 条</span>
                  </div>
                  <div className="dp-wm-related">
                    {relatedRules.map((rule) => (
                      <button
                        key={rule.id}
                        type="button"
                        onClick={() => {
                          setTab("rules");
                          setExpandedRule(rule.id);
                        }}
                      >
                        {rule.title}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : (
            <div className="dp-lib-empty">选一个世界节点。</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 视觉设定（设定图上传 / 删除）

interface WorldImagesProps {
  node: WorldNode;
  projectId?: string;
  onNotify?: (kind: "success" | "error" | "info", message: string) => void;
}

/**
 * 有项目上下文时走可编辑版本（上传 / 删除 + 刷新世界投影）；
 * 没有时退化成只读画廊（资料库被别处复用时不该出现写操作）。
 *
 * 分开两个组件是为了 hook 规则：`useQueryClient` 只在真的有 QueryClient 的
 * 宿主里跑，只读用法不需要 Provider。
 */
function WorldImages({ node, projectId, onNotify }: WorldImagesProps) {
  if (!projectId) {
    return <WorldImagesView nodeName={node.name} images={node.images} />;
  }
  return (
    <WorldImagesEditable
      node={node}
      projectId={projectId}
      {...(onNotify ? { onNotify } : {})}
    />
  );
}

interface WorldImagesViewProps {
  nodeName: string;
  images: readonly WorldImage[];
  /** 每张图右上角的操作（可编辑时是「×」+ 二次确认）。 */
  renderTileAction?: (item: WorldImageItem) => React.ReactNode;
  /** 画廊上方的操作区（上传全景图 / 其他视角）。 */
  controls?: React.ReactNode;
}

/** 纯展示：按 kind 分组，点图开全屏看图（同组可 ← → 切换）；不碰网络、不碰状态。 */
function WorldImagesView({
  nodeName,
  images,
  renderTileAction,
  controls = null,
}: WorldImagesViewProps) {
  const groups = worldImageGroups(images);
  const imageViewer = useImageViewer();

  function open(group: (typeof groups)[number], item: WorldImageItem) {
    imageViewer.open(
      group.items.map((entry) => ({
        src: entry.src,
        title: `${nodeName} · ${entry.image.kind}`,
        subtitle: group.meta.label,
        // 「全景」按宽幅处理：整图铺满高度、拖动环视。
        panorama: entry.image.kind === "全景",
      })),
      group.items.findIndex((entry) => entry.index === item.index),
    );
  }

  if (groups.length === 0) {
    return (
      <div className="dp-wm-empty dp-wm-images-empty">
        <p>这个节点还没有设定图。</p>
        <p className="dp-wm-empty-hint">
          上传一张<b>全景图</b>，该场所有镜头共用这张背景；<b>站位图</b>
          只在提示词里描述人物位置，不作为参考图。
        </p>
        {controls}
      </div>
    );
  }

  return (
    <div className="dp-wm-images">
      {controls}
      {groups.map((group) => (
        <section
          key={group.meta.key}
          className={`dp-wm-img-group is-${group.meta.key}`}
          aria-label={`${nodeName} 的${group.meta.label}图`}
        >
          <div className="dp-wm-img-group-head">
            <b>{group.meta.label}</b>
            <span>{worldImageGroupNote(group)}</span>
            {group.meta.hint ? <em>{group.meta.hint}</em> : null}
          </div>
          <div className="dp-wm-gallery">
            {group.items.map((item) => {
              const alt = `${nodeName} · ${item.image.kind}`;
              return (
                <div className="dp-wm-img" key={`${item.image.key}-${item.index}`}>
                  <button
                    type="button"
                    className="dp-wm-shot"
                    aria-label={`查看 ${alt}`}
                    onClick={() => open(group, item)}
                  >
                    <img src={item.src} alt={alt} loading="lazy" />
                    <span>{item.image.kind}</span>
                  </button>
                  {renderTileAction?.(item)}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

interface WorldImagesEditableProps {
  node: WorldNode;
  projectId: string;
  onNotify?: (kind: "success" | "error" | "info", message: string) => void;
}

/**
 * 可编辑版本。
 *
 * 上传 / 删除的响应就是更新后的 `WorldNode`：先本地覆盖（立刻可见），
 * 再 invalidate `["world", projectId]` 让投影重新拉一遍。
 * `node` 引用一变（父级拿到新投影 / 切了节点）就把本地覆盖丢掉，避免压住别人的修改。
 */
function WorldImagesEditable({ node, projectId, onNotify }: WorldImagesEditableProps) {
  const queryClient = useQueryClient();
  const [override, setOverride] = useState<WorldNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [otherKind, setOtherKind] = useState<WorldImageKind>("景观");
  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  useEffect(() => {
    setOverride(null);
    setConfirmIndex(null);
  }, [node]);

  const current = override && override.id === node.id ? override : node;

  async function upload(kind: WorldImageKind, file: File) {
    setBusy(true);
    try {
      const next = await uploadWorldImage(projectId, node.id, kind, file);
      setOverride(next);
      void queryClient.invalidateQueries({ queryKey: ["world", projectId] });
      onNotify?.("success", `「${node.name}」${kind}图已上传`);
    } catch (error) {
      onNotify?.("error", errorMessage(error, "设定图上传失败"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(index: number) {
    setDeletingIndex(index);
    try {
      const next = await deleteWorldImage(projectId, node.id, index);
      setOverride(next);
      setConfirmIndex(null);
      void queryClient.invalidateQueries({ queryKey: ["world", projectId] });
      onNotify?.("success", `已删除「${node.name}」的设定图`);
    } catch (error) {
      onNotify?.("error", errorMessage(error, "设定图删除失败"));
    } finally {
      setDeletingIndex(null);
    }
  }

  const controls = (
    <div className="dp-wm-upload">
      <label className="dp-btn sm primary dp-wm-upload-main">
        ＋ 上传全景图
        <input
          type="file"
          accept="image/*"
          className="dp-sr"
          aria-label={`给${current.name}上传全景图`}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload("全景", file);
            // 允许重复选同一个文件。
            event.target.value = "";
          }}
        />
      </label>
      <label className="dp-wm-kind">
        <span>其他视角</span>
        <select
          value={otherKind}
          aria-label="其他视角"
          disabled={busy}
          onChange={(event) => setOtherKind(event.target.value as WorldImageKind)}
        >
          {WORLD_OTHER_IMAGE_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
      </label>
      <label className="dp-btn sm">
        上传{otherKind}
        <input
          type="file"
          accept="image/*"
          className="dp-sr"
          aria-label={`给${current.name}上传${otherKind}`}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(otherKind, file);
            event.target.value = "";
          }}
        />
      </label>
      {busy ? <span className="dp-wm-upload-busy">上传中…</span> : null}
    </div>
  );

  return (
    <WorldImagesView
      nodeName={current.name}
      images={current.images}
      controls={controls}
      renderTileAction={(item) => (
        <>
          <button
            type="button"
            className="dp-wm-img-del"
            aria-label={`删除 ${current.name} · ${item.image.kind}`}
            disabled={busy || deletingIndex !== null}
            onClick={() => setConfirmIndex(item.index)}
          >
            ×
          </button>
          {confirmIndex === item.index ? (
            <div
              className="dp-wm-img-confirm"
              role="group"
              aria-label={`确认删除 ${current.name} · ${item.image.kind}`}
            >
              <span>删除这张？</span>
              <button
                type="button"
                className="dp-btn sm danger"
                disabled={deletingIndex === item.index}
                onClick={() => void remove(item.index)}
              >
                {deletingIndex === item.index ? "删除中…" : "确认删除"}
              </button>
              <button
                type="button"
                className="dp-btn sm ghost"
                onClick={() => setConfirmIndex(null)}
              >
                取消
              </button>
            </div>
          ) : null}
        </>
      )}
    />
  );
}

function countSceneRefs(
  nodes: readonly WorldNode[],
  nodeId: string,
): number {
  const seen = new Set<string>();
  const stack = [nodeId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (!id) continue;
    const node = worldNodeById(nodes, id);
    if (!node) continue;
    for (const sceneId of node.sceneIds) seen.add(sceneId);
    for (const child of nodes.filter((item) => item.parentId === id)) stack.push(child.id);
  }
  return seen.size;
}

function WorldRules({
  rules,
  outputs,
  query,
  onQuery,
  expandedRule,
  onExpand,
  onGenerate,
}: {
  rules: readonly WorldRule[];
  outputs: readonly OutputRef[];
  query: string;
  onQuery: (value: string) => void;
  expandedRule: string | null;
  onExpand: (id: string | null) => void;
  onGenerate?: (() => void) | undefined;
}) {
  const q = query.trim().toLowerCase();
  const filtered = rules.filter((rule) => {
    if (!q) return true;
    return `${rule.title} ${rule.category} ${rule.body}`.toLowerCase().includes(q);
  });

  return (
    <section className="dp-world-rules">
      <div className="dp-world-rules-head">
        <div>
          <div className="dp-lib-kicker">世界规则 · 项目级</div>
          <b>设定与版本</b>
        </div>
        <span>{rules.length} 条 · 默认全项目 · 可标产出线</span>
      </div>
      <label className="dp-lib-search dp-rules-search">
        <span aria-hidden="true">⌕</span>
        <input
          value={query}
          placeholder="搜索规则 / 关键词…"
          aria-label="搜索世界规则"
          onChange={(event) => onQuery(event.target.value)}
        />
      </label>

      {filtered.length === 0 ? (
        <div className="dp-doc-empty">
          <p className="dp-doc-empty-title">还没有世界规则</p>
          <p className="dp-doc-empty-text">
            规则来自 <code>world/rules.json</code>；默认全项目，也可以只标到某几条产出线。
          </p>
          {onGenerate ? (
            <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
              用 Agent 生成
            </button>
          ) : null}
        </div>
      ) : (
        <div className="dp-world-grid">
          {filtered.map((rule) => {
            const expanded = expandedRule === rule.id;
            const latest = rule.versions[rule.versions.length - 1];
            return (
              <article key={rule.id} className={`dp-world-card ${expanded ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="dp-world-card-main"
                  aria-expanded={expanded}
                  onClick={() => onExpand(expanded ? null : rule.id)}
                >
                  <div className="dp-wc-head">
                    <span className="dp-wc-cat">{rule.category}</span>
                    <span className="dp-wc-ver">
                      {rule.version} · {rule.versions.length} 版
                    </span>
                  </div>
                  <div className="dp-wc-title">{rule.title}</div>
                  <div className="dp-wc-body">{rule.body.slice(0, 120)}</div>
                </button>
                {expanded ? (
                  <div className="dp-world-card-body">
                    <p>{rule.body}</p>
                    <div className="dp-version-history">
                      {[...rule.versions].reverse().map((version) => (
                        <div key={`${rule.id}-${version.version}`} className="dp-vh-row">
                          <b>{version.version}</b>
                          <span>{formatDate(version.createdAt)}</span>
                          <p>{version.note ?? "—"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="dp-wc-foot">
                  <span className={`dp-wc-scope ${rule.scope.all ? "is-all" : ""}`}>
                    {worldRuleScopeNames(rule, outputs).join(" / ")}
                  </span>
                  <span>{latest ? formatDate(latest.createdAt) : "—"}</span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
